import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { query, orderBy, getFirestore, collection, addDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

// Firebase configuration (replace with your Firebase project config)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Predefined crypto community-related usernames
const cryptoUsernames = [
  'Hodler',
  'Whale',
  'Satoshi',
  'Blockchainer',
  'CryptoKing',
  'MiningMaster',
  'Bitcoiner',
  'Etherean',
  'Altcoiner',
  'Trader',
  'Investor',
];

// Mapping colors to usernames
const usernameColors = {
  'Hodler': '#FF5733',
  'Whale': '#33FF57',
  'Satoshi': '#3357FF',
  'Blockchainer': '#F1C40F',
  'CryptoKing': '#9B59B6',
  'MiningMaster': '#E74C3C',
  'Bitcoiner': '#2ECC71',
  'Etherean': '#3498DB',
  'Altcoiner': '#F39C12',
  'Trader': '#8E44AD',
  'Investor': '#34495E',
};

const SimpleChatApp = () => {
  const [username, setUsername] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const chatEndRef = useRef(null);

  // Auto-assign random crypto username on first load
  useEffect(() => {
    if (!localStorage.getItem('username')) {
      const randomUsername = cryptoUsernames[Math.floor(Math.random() * cryptoUsernames.length)];
      localStorage.setItem('username', randomUsername);  // Save to localStorage
      setUsername(randomUsername);
    } else {
      setUsername(localStorage.getItem('username'));
    }
  }, []);

  useEffect(() => {
    const colRef = collection(db, 'messages');
    const q = query(colRef, orderBy('timestamp')); // Create a query with ordering

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(fetchedMessages);
    });

    return () => unsubscribe(); // Cleanup the subscription on unmount
  }, []);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Send new message to Firestore
  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      const colRef = collection(db, 'messages');
      await addDoc(colRef, {
        username,
        message: newMessage,
        timestamp: serverTimestamp(),
      });
      setNewMessage(''); // Clear input after sending
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
      <h1>The Gen₿ Chat</h1>

      {/* Show assigned username */}
      <p>Your username: <strong>{username}</strong></p>

      {/* Messages display */}
      <div className="chat-container" style={{ height: '400px', overflowY: 'auto', padding: '20px' }}>
        {messages.map((msg) => (
          <div key={msg.id} style={{ marginBottom: '10px', textAlign: 'left' }}>
            <strong style={{ color: usernameColors[msg.username] || "#000" }}>{msg.username}</strong>: {msg.message}
          </div>
        ))}
        {/* The element we'll scroll to */}
        <div ref={chatEndRef}></div>
      </div>

      <div className="input-area">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }}
          placeholder="Type a message..."
          className="p-2 rounded-md border border-gray-300 focus:outline-none focus:ring focus:border-blue-500 bg-gray-800 text-gray-200"
        />
        <button onClick={handleSendMessage}>Send</button>
      </div>
    </div>
  );
};

export default SimpleChatApp;
